import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import AdminLayout from "../layouts/adminLayout/AdminLayout";
import {
  getUnions,
  createUnion,
  updateUnion,
  deleteUnion,
  getDivisions,
  getDistricts,
  getUpazilas,
} from "../config/api/unionApi";

export default function UnionList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filterDivisionId, setFilterDivisionId] = useState("");
  const [filterDistrictId, setFilterDistrictId] = useState("");
  const [filterUpazillaId, setFilterUpazillaId] = useState("");

  const [divisions, setDivisions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [upazilas, setUpazilas] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    division_id: "",
    district_id: "",
    upazilla_id: "",
    name: "",
    bn_name: "",
  });

  const [formDistricts, setFormDistricts] = useState([]);
  const [formUpazilas, setFormUpazilas] = useState([]);

  const fetchUnions = useCallback(async () => {
    try {
      setLoading(true);

      const res = await getUnions({
        page: page + 1,
        per_page: pageSize,
        search: appliedSearch || undefined,
        upazilla_id: filterUpazillaId,
    });

      setRows(res?.data?.data || []);
      setRowCount(res?.data?.total || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load unions");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedSearch, filterUpazillaId]);

  const fetchDivisions = async () => {
    try {
      const res = await getDivisions();
      setDivisions(res?.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load divisions");
    }
  };

  useEffect(() => {
    fetchDivisions();
  }, []);

  useEffect(() => {
    fetchUnions();
  }, [fetchUnions]);

  const handleSearch = () => {
    setPage(0);
    setAppliedSearch(search);
  };

  const handleFilterDivisionChange = async (value) => {
    setFilterDivisionId(value);
    setFilterDistrictId("");
    setFilterUpazillaId("");
    setDistricts([]);
    setUpazilas([]);
    setPage(0);

    if (value) {
      try {
        const res = await getDistricts(value);
        setDistricts(res?.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load districts");
      }
    }
  };

  const handleFilterDistrictChange = async (value) => {
    setFilterDistrictId(value);
    setFilterUpazillaId("");
    setUpazilas([]);
    setPage(0);

    if (value) {
      try {
        const res = await getUpazilas(value);
        setUpazilas(res?.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load upazilas");
      }
    }
  };

  const handleFilterUpazillaChange = (value) => {
    setFilterUpazillaId(value);
    setPage(0);
  };

  const handleOpenCreate = () => {
    setEditId(null);
    setFormData({
      division_id: "",
      district_id: "",
      upazilla_id: "",
      name: "",
      bn_name: "",
    });
    setFormDistricts([]);
    setFormUpazilas([]);
    setOpenModal(true);
  };

  const handleOpenEdit = (row) => {
    setEditId(row.id);
    setFormData({
      division_id: "",
      district_id: "",
      upazilla_id: row.upazilla_id || "",
      name: row.name || "",
      bn_name: row.bn_name || "",
    });
    setFormDistricts([]);
    setFormUpazilas([]);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditId(null);
    setFormData({
      division_id: "",
      district_id: "",
      upazilla_id: "",
      name: "",
      bn_name: "",
    });
    setFormDistricts([]);
    setFormUpazilas([]);
  };

  const handleFormDivisionChange = async (value) => {
    setFormData((prev) => ({
      ...prev,
      division_id: value,
      district_id: "",
      upazilla_id: "",
    }));
    setFormDistricts([]);
    setFormUpazilas([]);

    if (value) {
      try {
        const res = await getDistricts(value);
        setFormDistricts(res?.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load districts");
      }
    }
  };

  const handleFormDistrictChange = async (value) => {
    setFormData((prev) => ({
      ...prev,
      district_id: value,
      upazilla_id: "",
    }));
    setFormUpazilas([]);

    if (value) {
      try {
        const res = await getUpazilas(value);
        setFormUpazilas(res?.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load upazilas");
      }
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.upazilla_id || !formData.name) {
        toast.error("Upazila and name are required");
        return;
      }

      const payload = {
        upazilla_id: formData.upazilla_id,
        name: formData.name,
        bn_name: formData.bn_name,
      };

      if (editId) {
        await updateUnion(editId, {
          ...payload,
          _method: "PUT",
        });
        toast.success("Union updated successfully");
      } else {
        await createUnion(payload);
        toast.success("Union created successfully");
      }

      handleCloseModal();
      fetchUnions();
    } catch (error) {
      console.error(error);
      toast.error("Failed to save union");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete?")) return;

    try {
      await deleteUnion(id);
      toast.success("Union deleted successfully");

      const isLastRowOnPage = rows.length === 1 && page > 0;
      if (isLastRowOnPage) {
        setPage((prev) => prev - 1);
      } else {
        fetchUnions();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete union");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "name", headerName: "Name", width: 180 },
    {
      field: "bn_name",
      headerName: "Bangla Name",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    { field: "upazilla_id", headerName: "Upazila ID", width: 120 },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box className="flex gap-2">
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenEdit(params.row)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <AdminLayout title={"Union"}>
      <Box className="p-6 bg-white !rounded">
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-5">
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Select
            size="small"
            displayEmpty
            value={filterDivisionId}
            onChange={(e) => handleFilterDivisionChange(e.target.value)}
          >
            <MenuItem value="">Select Division</MenuItem>
            {divisions.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            displayEmpty
            value={filterDistrictId}
            onChange={(e) => handleFilterDistrictChange(e.target.value)}
            disabled={!filterDivisionId}
          >
            <MenuItem value="">Select District</MenuItem>
            {districts.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            displayEmpty
            value={filterUpazillaId}
            onChange={(e) => handleFilterUpazillaChange(e.target.value)}
            disabled={!filterDistrictId}
          >
            <MenuItem value="">Select Upazila</MenuItem>
            {upazilas.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>

          <Box className="flex gap-2">
            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
            <Button variant="outlined" onClick={handleOpenCreate}>
              Add
            </Button>
          </Box>
        </div>

        <div style={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              if (model.page !== page) setPage(model.page);
              if (model.pageSize !== pageSize) {
                setPageSize(model.pageSize);
                setPage(0);
              }
            }}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
          />
        </div>

        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{editId ? "Edit Union" : "Add Union"}</DialogTitle>
          <DialogContent>
            <Box className="grid grid-cols-1 gap-4 mt-3">
              <Select
                size="small"
                displayEmpty
                value={formData.division_id}
                onChange={(e) => handleFormDivisionChange(e.target.value)}
              >
                <MenuItem value="">Select Division</MenuItem>
                {divisions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                displayEmpty
                value={formData.district_id}
                onChange={(e) => handleFormDistrictChange(e.target.value)}
                disabled={!formData.division_id}
              >
                <MenuItem value="">Select District</MenuItem>
                {formDistricts.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                displayEmpty
                value={formData.upazilla_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    upazilla_id: e.target.value,
                  }))
                }
                disabled={!formData.district_id}
              >
                <MenuItem value="">Select Upazila</MenuItem>
                {formUpazilas.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>

              <TextField
                label="Union Name"
                size="small"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />

              <TextField
                label="Bangla Name"
                size="small"
                value={formData.bn_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    bn_name: e.target.value,
                  }))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editId ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
}
